/**
 * Confirmation watcher for the boot-splash watchdog — it decides whether a
 * splash that is still up at the ~15s checkpoint is a genuine wedge or a boot
 * that was merely slow.
 *
 * Armed INSTEAD of reporting (SplashScreenLoader). It watches the veil for one
 * more window and fires EXACTLY ONE of:
 *
 * - `onRecovered(ms)` — the veil cleared inside the window. The app rendered;
 *   the wedge was a false alarm (the CUSTOMER-UI-3Z Mode B class). The caller
 *   records an observable-only signal, never a Sentry issue.
 * - `onStillStuck()` — the window elapsed with the veil STILL up. Now it is a
 *   confirmed wedge and the caller commits the error-level event.
 *
 * The earlier design reported the error at the checkpoint and then emitted a
 * paired "recovered" event, leaving triage to subtract one from the other.
 * Deciding BEFORE reporting means `boot-splash-stuck` only ever fires for a
 * boot that really failed — at the cost of a later report, which is acceptable
 * because a genuine wedge keeps the splash up indefinitely.
 *
 * A MutationObserver on the veil's `style` attribute (removeLoader hides it via
 * inline opacity/display) rather than a poll, so `recoveredAfterMs` is not
 * quantized to a poll interval. The observer is an OPTIMISATION for recovery
 * precision only — the deadline check below is what guarantees a verdict, so a
 * browser without MutationObserver (or an arming failure) still reports the
 * wedge.
 */

export interface SplashRecoveryWatcherOptions {
  /** The #globalLoader veil element to observe. */
  loader: HTMLElement | null;
  /** Style-based visibility re-check (mirrors SplashScreenLoader's). */
  isSplashVisible: () => boolean;
  /**
   * Fired AT MOST ONCE when the veil clears inside the window. The argument is
   * ms since arming (i.e. since the ~15s checkpoint), not since page load.
   */
  onRecovered: (recoveredAfterMs: number) => void;
  /**
   * Fired AT MOST ONCE when the window elapses with the veil still up — the
   * confirmed wedge. Exactly one of `onRecovered` / `onStillStuck` runs, unless
   * the caller disarms first (effect cleanup).
   */
  onStillStuck: () => void;
  /** How long to watch before delivering the verdict. */
  windowMs: number;
}

export interface SplashRecoveryWatcher {
  /** Disconnect the observer + cap timer; safe to call repeatedly. */
  disarm: () => void;
}

export function armSplashRecoveryWatcher({
  loader,
  isSplashVisible,
  onRecovered,
  onStillStuck,
  windowMs,
}: SplashRecoveryWatcherOptions): SplashRecoveryWatcher {
  let observer: MutationObserver | undefined;
  let capTimer: ReturnType<typeof setTimeout> | undefined;
  let settled = false;

  const disarm = (): void => {
    settled = true;
    observer?.disconnect();
    observer = undefined;
    if (capTimer !== undefined) clearTimeout(capTimer);
    capTimer = undefined;
  };

  const emitRecovered = (recoveredAfterMs: number): void => {
    if (settled) return;
    disarm();
    onRecovered(recoveredAfterMs);
  };

  // The window elapsed. Re-check rather than assume a wedge: the observer can
  // miss a clear that didn't go through a `style` mutation (e.g. the node was
  // replaced), and a false wedge report is exactly what this rewrite exists to
  // eliminate. A recovery caught only here reports `windowMs` as an upper
  // bound on the true recovery time.
  const emitVerdict = (): void => {
    if (settled) return;
    let stillStuck = true;
    try {
      stillStuck = isSplashVisible();
    } catch {
      // Can't tell — assume stuck. Dropping a real wedge silently is the one
      // outcome this watchdog exists to prevent.
    }
    disarm();
    if (stillStuck) onStillStuck();
    else onRecovered(windowMs);
  };

  try {
    const armedAt = Date.now();

    // Near-never fires (the caller checks and arms in one synchronous
    // macrotask) — cheap insurance against a removeLoader that ran between the
    // caller's own visibility check and this arming.
    if (!isSplashVisible()) {
      emitRecovered(0);
      return { disarm };
    }

    if (loader && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        if (!isSplashVisible()) emitRecovered(Date.now() - armedAt);
      });
      observer.observe(loader, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }
    capTimer = setTimeout(emitVerdict, windowMs);
    return { disarm };
  } catch {
    // Arming the observer failed. Fall back to the bare deadline check so the
    // verdict is still delivered — telemetry machinery must never break the
    // boot path, but it must not silently swallow a wedge either.
    observer?.disconnect();
    observer = undefined;
    if (capTimer === undefined) capTimer = setTimeout(emitVerdict, windowMs);
    return { disarm };
  }
}
